import { AssetImage } from "gql/generated";

/**
 * Determines if an image should use "contain" instead of "cover".
 *
 * The core heuristic: if any detected text block sits in the region that
 * cover-mode would crop away, the image is "not croppable" and should be
 * shown with contain so nothing important is lost.
 *
 * When container dimensions are provided the crop zone is calculated
 * from a centre crop baseline, then expanded by a safety factor to
 * account for g_auto shifting the crop window toward either edge.
 *
 * Detection is powered by cld-text blocks with bounding boxes. When
 * bounding boxes are available, an edge-proximity check determines if
 * text falls in the crop zone. When only the boolean `hasText` flag is
 * present (no bounding boxes), we trust the boolean directly.
 */
export const shouldUseContainForImage = (
  image?: Partial<AssetImage> | null,
  isLogo?: boolean,
  containerW?: number,
  containerH?: number,
): boolean => {
  if (isLogo) return true;
  if (!image?.detectedObjects) return false;

  const textBlocks = (image.detectedObjects as any)?.text || [];

  if (textBlocks.length === 0) {
    return !!image.detectedObjects.hasText;
  }

  // Images with very few dominant colors (≤ 3) and detected text are
  // overwhelmingly graphics or logos on a solid background, not
  // photographs. Cover mode zooms in unnaturally on these.
  const MAX_GRAPHIC_COLORS = 3;
  if (
    image.detectedObjects.hasText &&
    (image.colors?.length ?? 99) <= MAX_GRAPHIC_COLORS
  ) {
    return true;
  }

  const imgW = image.width || 0;
  const imgH = image.height || 0;
  if (!imgW || !imgH) return !!image.detectedObjects.hasText;

  const margins = getCropMargins(imgW, imgH, containerW, containerH);

  // g_auto can shift the crop window away from centre, so text that
  // barely clears the centre-crop zone may still be clipped. A 1.5×
  // factor on each margin accounts for moderate g_auto shifts.
  const G_AUTO_SAFETY = 1.5;
  const safeTop = margins.top * G_AUTO_SAFETY;
  const safeBottom = margins.bottom * G_AUTO_SAFETY;
  const safeLeft = margins.left * G_AUTO_SAFETY;
  const safeRight = margins.right * G_AUTO_SAFETY;

  for (const block of textBlocks) {
    if (!block.boundingBox || block.boundingBox.length !== 4) continue;
    const [x, y, w, h] = block.boundingBox;

    if (
      x < safeLeft ||
      y < safeTop ||
      x + w > imgW - safeRight ||
      y + h > imgH - safeBottom
    ) {
      return true;
    }
  }

  if (textSpanFillsVisibleArea(textBlocks, imgW, imgH, containerW, containerH))
    return true;

  return false;
};

/**
 * Calculates how many pixels would be cropped from each edge when the
 * image is displayed in cover mode (centre crop).
 *
 * If the image is wider than the container ratio the sides are clipped;
 * if it is taller the top/bottom are clipped.
 *
 * Falls back to a 10 % margin on every edge when no container size is
 * available.
 */
function getCropMargins(
  imgW: number,
  imgH: number,
  containerW?: number,
  containerH?: number,
): { top: number; bottom: number; left: number; right: number } {
  const FALLBACK_EDGE_FRACTION = 0.1;

  if (!containerW || !containerH) {
    return {
      top: imgH * FALLBACK_EDGE_FRACTION,
      bottom: imgH * FALLBACK_EDGE_FRACTION,
      left: imgW * FALLBACK_EDGE_FRACTION,
      right: imgW * FALLBACK_EDGE_FRACTION,
    };
  }

  const imgRatio = imgW / imgH;
  const containerRatio = containerW / containerH;

  if (imgRatio > containerRatio) {
    const visibleW = imgH * containerRatio;
    const cropX = (imgW - visibleW) / 2;
    return { top: 0, bottom: 0, left: cropX, right: cropX };
  }

  const visibleH = imgW / containerRatio;
  const cropY = (imgH - visibleH) / 2;
  return { top: cropY, bottom: cropY, left: 0, right: 0 };
}

/**
 * Returns true when the combined extent of all text blocks spans more
 * than 80 % of the visible region in the crop direction.
 *
 * The per-block edge check catches text that literally sits in the crop
 * zone. This check catches the subtler case: centered text that spans
 * most of the image, leaving almost no breathing room once the cover
 * crop is applied. Typical example: a square logo with vertically
 * stacked text displayed in a landscape container.
 */
const TEXT_SPAN_FILL_THRESHOLD = 0.8;

function textSpanFillsVisibleArea(
  textBlocks: any[],
  imgW: number,
  imgH: number,
  containerW?: number,
  containerH?: number,
): boolean {
  if (!containerW || !containerH) return false;

  const imgRatio = imgW / imgH;
  const containerRatio = containerW / containerH;

  if (Math.abs(imgRatio - containerRatio) < 0.01) return false;

  const isHorizontalCrop = imgRatio > containerRatio;

  let min = Infinity;
  let max = -Infinity;

  for (const block of textBlocks) {
    if (!block.boundingBox || block.boundingBox.length !== 4) continue;
    const [x, y, w, h] = block.boundingBox;
    if (isHorizontalCrop) {
      min = Math.min(min, x);
      max = Math.max(max, x + w);
    } else {
      min = Math.min(min, y);
      max = Math.max(max, y + h);
    }
  }

  if (min === Infinity) return false;

  const visibleDimension = isHorizontalCrop
    ? imgH * containerRatio
    : imgW / containerRatio;

  return (max - min) / visibleDimension > TEXT_SPAN_FILL_THRESHOLD;
}
